import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { Constraints, Platform } from '../../../constraints';
import { AndroidBridgeBase } from '../../../mobile/api-services/bridge/android-bridge-base';
import { Page, Pageable } from '../../../utils/pageable';
import {
  AnswerHolderBase,
  ExerciseSessionByPathRequest,
  ExerciseSessionRecreateRequest,
  ExerciseSessionStatus,
  ItemAvailability,
  V3Chapter,
  V3ExamSession,
  V3ExamSessionPart,
  V3ExamSessionPartStatus,
  V3ExamSessionStatus,
  V3ExerciseSession,
  V3ExerciseSessionQuestion,
  V3ExerciseSet,
} from '../../model/v3/cspa';
import { CspaRestV3 } from './cspa-rest-v3';
import { LogsService } from '../../../utils/services/logs.service';

@Injectable({
  providedIn: 'root',
})
export class CspaRestV3AndroidService implements CspaRestV3 {
  private readonly endpoint = Constraints.androidGenericInternalApiEndpoint;

  constructor(
    private logger: LogsService,
    private http: HttpClient,
    private bridgeBase: AndroidBridgeBase,
  ) {}

  private peopleUrl(personId: number, path = ''): string {
    return `${this.endpoint}/platform/${Platform.Events}/v1/people/${personId}${path}`;
  }

  private eventsUrl(path: string): string {
    return `${this.endpoint}/platform/${Platform.Events}/v1${path}`;
  }

  private static buildRequestIdHeader(id: number): HttpHeaders {
    return new HttpHeaders({ 'X-Request-Id': id.toString() });
  }

  private nextRequestHeaders(contentTypeJson = false): HttpHeaders {
    let headers = CspaRestV3AndroidService.buildRequestIdHeader(this.bridgeBase.getNextRequestId());
    if (contentTypeJson) {
      headers = headers.append('Content-Type', 'application/json');
    }
    return headers;
  }

  isOffline(): Observable<boolean> {
    return this.bridgeBase.callNative<boolean>(id => window.nativeBridge.isOffline(id)).pipe(
      tap(id => this.logger.log(`isOffline - id = ${id}`))
    );
  }

  listExerciseSets(): Observable<V3ExerciseSet[]> {
    this.logger.log('cspa (v3) listExerciseSets');
    return this.http.get<V3ExerciseSet[]>(
      this.eventsUrl('/struct/sets'),
      { headers: this.nextRequestHeaders() },
    );
  }

  getChapters(path: string, depth: number): Observable<V3Chapter[]> {
    this.logger.log(`cspa (v3) getChapters path=${path} depth=${depth}`);
    const params = new HttpParams().set('depth', depth.toString());
    return this.http.get<V3Chapter[]>(
      this.eventsUrl(`/struct/${encodeURIComponent(path)}`),
      { headers: this.nextRequestHeaders(), params },
    );
  }

  listPersonAvailabilities(
    personId: number,
    path?: string | null,
    depth?: number | null,
  ): Observable<ItemAvailability[]> {
    this.logger.log(`cspa (v3) listPersonAvailabilities personId=${personId}`);
    let params = new HttpParams();
    if (path != null) {
      params = params.set('path', path);
    }
    if (depth != null) {
      params = params.set('depth', depth.toString());
    }
    return this.http.get<ItemAvailability[]>(
      this.peopleUrl(personId, '/exercise-progress-tree'),
      { headers: this.nextRequestHeaders(), params },
    );
  }

  findSessions(
    personId: number,
    pathPrefix?: string | null,
    pageable?: Pageable,
  ): Observable<Page<V3ExerciseSession>> {
    this.logger.log(`cspa (v3) findSessions personId=${personId}`);
    let params = pageable ? pageable.appendParams(new HttpParams()) : new HttpParams();
    if (pathPrefix != null) {
      params = params.set('pathPrefix', pathPrefix);
    }
    return this.http.get<Page<V3ExerciseSession>>(
      this.peopleUrl(personId, '/exercise-sessions'),
      { headers: this.nextRequestHeaders(), params },
    );
  }

  createSession(personId: number, request: ExerciseSessionByPathRequest): Observable<V3ExerciseSession> {
    this.logger.log(`cspa (v3) createSession personId=${personId}`);
    return this.http.post<V3ExerciseSession>(
      this.peopleUrl(personId, '/exercise-sessions'),
      request,
      { headers: this.nextRequestHeaders() },
    );
  }

  recreateSession(personId: number, request: ExerciseSessionRecreateRequest): Observable<V3ExerciseSession> {
    this.logger.log(`cspa (v3) recreateSession personId=${personId}`);
    const params = new HttpParams().append('recreate', '');
    return this.http.post<V3ExerciseSession>(
      this.peopleUrl(personId, '/exercise-sessions'),
      request,
      { headers: this.nextRequestHeaders(), params },
    );
  }

  getSession(personId: number, uuid: string): Observable<V3ExerciseSession> {
    this.logger.log(`cspa (v3) getSession personId=${personId} uuid=${uuid}`);
    return this.http.get<V3ExerciseSession>(
      this.peopleUrl(personId, `/exercise-sessions/${uuid}`),
      { headers: this.nextRequestHeaders() },
    );
  }

  getSessionQuestions(personId: number, uuid: string): Observable<V3ExerciseSessionQuestion[]> {
    this.logger.log(`cspa (v3) getSessionQuestions personId=${personId} uuid=${uuid}`);
    return this.http.get<V3ExerciseSessionQuestion[]>(
      this.peopleUrl(personId, `/exercise-sessions/${uuid}/questions`),
      { headers: this.nextRequestHeaders() },
    );
  }

  updateSessionStatus(
    personId: number,
    uuid: string,
    status: ExerciseSessionStatus,
  ): Observable<V3ExerciseSession> {
    this.logger.log(`cspa (v3) updateSessionStatus personId=${personId} uuid=${uuid}`);
    return this.http.patch<V3ExerciseSession>(
      this.peopleUrl(personId, `/exercise-sessions/${uuid}/status`),
      JSON.stringify(status),
      { headers: this.nextRequestHeaders(true) },
    );
  }

  submitQuestionAnswer(
    personId: number,
    uuid: string,
    questionNumber: number,
    answer: AnswerHolderBase,
  ): Observable<void> {
    this.logger.log(`cspa (v3) submitQuestionAnswer personId=${personId} uuid=${uuid} q=${questionNumber}`);
    return this.http.post<void>(
      this.peopleUrl(personId, `/exercise-sessions/${uuid}/questions/${questionNumber}/answer`),
      answer,
      { headers: this.nextRequestHeaders() },
    );
  }

  findExamSessions(personId: number, pathPrefix?: string | null): Observable<V3ExamSession[]> {
    this.logger.log(`cspa (v3) findExamSessions personId=${personId}`);
    let params = new HttpParams();
    if (pathPrefix != null) {
      params = params.set('pathPrefix', pathPrefix);
    }
    return this.http.get<V3ExamSession[]>(
      this.peopleUrl(personId, '/exam-sessions'),
      { headers: this.nextRequestHeaders(), params },
    );
  }

  createExamSession(personId: number, request: { path: string }): Observable<V3ExamSession> {
    this.logger.log(`cspa (v3) createExamSession personId=${personId}`);
    return this.http.post<V3ExamSession>(
      this.peopleUrl(personId, '/exam-sessions'),
      request,
      { headers: this.nextRequestHeaders() },
    );
  }

  getExamSession(personId: number, uuid: string): Observable<V3ExamSession> {
    this.logger.log(`cspa (v3) getExamSession personId=${personId} uuid=${uuid}`);
    return this.http.get<V3ExamSession>(
      this.peopleUrl(personId, `/exam-sessions/${uuid}`),
      { headers: this.nextRequestHeaders() },
    );
  }

  updateExamSessionStatus(
    personId: number,
    uuid: string,
    status: V3ExamSessionStatus,
  ): Observable<V3ExamSession> {
    this.logger.log(`cspa (v3) updateExamSessionStatus personId=${personId} uuid=${uuid}`);
    return this.http.patch<V3ExamSession>(
      this.peopleUrl(personId, `/exam-sessions/${uuid}/status`),
      JSON.stringify(status),
      { headers: this.nextRequestHeaders(true) },
    );
  }

  findExamSessionsParts(personId: number, uuid: string): Observable<V3ExamSessionPart[]> {
    this.logger.log(`cspa (v3) findExamSessionsParts personId=${personId} uuid=${uuid}`);
    return this.http.get<V3ExamSessionPart[]>(
      this.peopleUrl(personId, `/exam-sessions/${uuid}/parts`),
      { headers: this.nextRequestHeaders() },
    );
  }

  getPartQuestions(personId: number, uuid: string, partNum: number): Observable<V3ExerciseSessionQuestion[]> {
    this.logger.log(`cspa (v3) getPartQuestions personId=${personId} uuid=${uuid} part=${partNum}`);
    return this.http.get<V3ExerciseSessionQuestion[]>(
      this.peopleUrl(personId, `/exam-sessions/${uuid}/parts/${partNum}/questions`),
      { headers: this.nextRequestHeaders() },
    );
  }

  updatePartStatus(
    personId: number,
    uuid: string,
    partNum: number,
    status: V3ExamSessionPartStatus,
  ): Observable<V3ExamSessionPart> {
    this.logger.log(`cspa (v3) updatePartStatus personId=${personId} uuid=${uuid} part=${partNum}`);
    return this.http.patch<V3ExamSessionPart>(
      this.peopleUrl(personId, `/exam-sessions/${uuid}/parts/${partNum}/status`),
      JSON.stringify(status),
      { headers: this.nextRequestHeaders(true) },
    );
  }

  submitPartQuestionAnswer(
    personId: number,
    uuid: string,
    partNum: number,
    questionNumber: number,
    answer: AnswerHolderBase,
  ): Observable<void> {
    this.logger.log(`cspa (v3) submitPartQuestionAnswer personId=${personId} uuid=${uuid} part=${partNum} q=${questionNumber}`);
    return this.http.put<void>(
      this.peopleUrl(personId, `/exam-sessions/${uuid}/parts/${partNum}/questions/${questionNumber}/answer`),
      answer,
      { headers: this.nextRequestHeaders() },
    );
  }
}
